import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrolleZu } from "@/lib/scroll";

const links = [
  { label: "Warum wir", href: "#warum" },
  { label: "Leistungen", href: "#leistungen" },
  { label: "Ablauf", href: "#ablauf" },
  { label: "Projekte", href: "#projekte" },
  { label: "Bewertungen", href: "#bewertungen" },
  { label: "Kontakt", href: "#kontakt" },
];

export default function Navigation({ introDone }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrolleZu(href, { duration: 1.8 });
  };

  return (
    <>
      <motion.nav
        data-testid="main-navigation"
        initial={{ y: -80, opacity: 0 }}
        animate={introDone ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed left-0 top-0 z-40 w-full transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "border-b border-[#D4AF37]/10 bg-[#050505]/70 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
          <a
            href="#"
            onClick={(e) => go(e, 0)}
            data-testid="nav-logo"
            className="flex items-baseline gap-2"
          >
            <span className="gold-text font-serif-accent text-2xl leading-none">S&F</span>
            <span className="hidden text-[10px] tracking-[0.4em] text-[#BDBDB6] sm:inline">
              WEBDESIGN
            </span>
          </a>

          <div className="hidden items-center gap-8 lg:flex">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="group relative text-sm tracking-[0.18em] text-[#BDBDB6] transition-colors duration-300 hover:text-[#F5F5F0]"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#D4AF37] transition-[width] duration-400 group-hover:w-full" />
              </a>
            ))}
            <a
              href="#kontakt"
              onClick={(e) => go(e, "#kontakt")}
              data-testid="nav-cta-button"
              className="rounded-full border border-[#D4AF37]/50 px-5 py-2 text-sm tracking-[0.14em] text-[#FFD700] transition-[background-color,box-shadow] duration-400 hover:bg-[#D4AF37]/10 hover:shadow-[0_0_24px_rgba(212,175,55,0.25)]"
            >
              Erstberatung
            </a>
          </div>

          <button
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-button"
            className="text-[#F5F5F0] lg:hidden"
            aria-label="Menü öffnen"
          >
            <Menu size={22} />
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-50 flex flex-col bg-[#050505]/95 backdrop-blur-2xl"
          >
            <div className="flex items-center justify-between px-6 py-4">
              <span className="gold-text font-serif-accent text-2xl">S&F</span>
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                className="text-[#F5F5F0]"
                aria-label="Menü schließen"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex flex-1 flex-col items-start justify-center gap-2 px-8">
              {links.map((l, i) => (
                <div key={l.href} className="overflow-hidden">
                  <motion.a
                    href={l.href}
                    onClick={(e) => go(e, l.href)}
                    data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ delay: 0.1 + i * 0.07, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="block font-display text-4xl font-bold uppercase tracking-tight text-[#F5F5F0] transition-colors hover:text-[#D4AF37]"
                  >
                    {l.label}
                  </motion.a>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
